import { PositionAnalysis } from '@/types/portfolio';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TrendingUp } from 'lucide-react';

interface PortfolioGainsPieChartProps {
  analyses: PositionAnalysis[];
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(var(--accent))',
];

export function PortfolioGainsPieChart({ analyses }: PortfolioGainsPieChartProps) {
  // Filter for stocks with positive added value (gains)
  const gainsData = analyses
    .filter(analysis => (analysis.addedValueChf || 0) > 0)
    .map(analysis => ({
      name: analysis.ticker,
      value: analysis.addedValueChf || 0,
      percentage: 0, // Will be calculated below
    }));

  console.log('PortfolioGainsPieChart - Total analyses:', analyses.length);
  console.log('PortfolioGainsPieChart - Stocks with gains:', gainsData.length);
  console.log('PortfolioGainsPieChart - Sample addedValueChf values:', 
    analyses.slice(0, 5).map(a => ({ ticker: a.ticker, addedValueChf: a.addedValueChf }))
  );

  if (gainsData.length === 0) {
    console.log('PortfolioGainsPieChart - No stocks with positive addedValueChf, not rendering chart');
    return null; // Don't show if no gains
  }

  // Calculate total gains
  const totalGains = gainsData.reduce((sum, item) => sum + item.value, 0);

  // Build chart data: top N + Others for readability
  const TOP = 12;
  const sorted = [...gainsData].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, TOP);
  const othersValue = sorted.slice(TOP).reduce((s, i) => s + i.value, 0);
  const withOthers = othersValue > 0 ? [...top, { name: 'Others', value: othersValue, percentage: 0 }] : top;

  // Calculate percentages for display/tooltip
  const chartData = withOthers.map(item => ({
    ...item,
    percentage: ((item.value / totalGains) * 100).toFixed(1),
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            CHF {data.value.toFixed(2)} ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
        {payload.map((entry: any, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-sm flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">
                {entry.payload.name}
              </p>
              <p className="text-xs text-muted-foreground">
                CHF {entry.payload.value.toFixed(2)} ({entry.payload.percentage}%)
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="w-full relative z-10 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Portfolio Gains Distribution
        </CardTitle>
        <CardDescription>
          Added value contribution by stock (CHF {totalGains.toFixed(2)} total)
        </CardDescription>
      </CardHeader>
      <CardContent className="relative">
        <div className="w-full h-[400px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                innerRadius={60}
                outerRadius={140}
                paddingAngle={2}
                fill="hsl(var(--primary))"
                dataKey="value"
                stroke="hsl(var(--background))"
                strokeWidth={2}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '20px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
